import type { NavigatorProps } from './Navigator.types';
import type { Question } from 'features/sessions/types';

import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import * as selectors from 'features/sessions/selectors';
// import { useGetSessionQuery } from 'features/sessions/api';
import './_navigator.scss';

type SortColumn = 'question' | 'status' | 'flagged';
type SortDirection = 'asc' | 'desc';
type SortState = { column: SortColumn; direction: SortDirection } | null;

// Status ordering matches Pearson VUE: ascending sort goes UnSeen → InComplete → Completed.
// This is a progress ordinal, not alphabetical.
const STATUS_ORDINAL: Record<Question['status'], number> = {
  UnSeen: 0,
  InComplete: 1,
  Flagged: 1,
  Completed: 2,
};

// Pearson VUE puts flagged rows first under ascending sort, so flagged maps to the lower ordinal.
const flagOrdinal = (flagged: 0 | 1) => (flagged ? 0 : 1);

export const Navigator = ({ navigateTo, onEnd }: NavigatorProps) => {
  // const currentSession = useSelector(selectors.selectCurrentSession);
  const currentSection = useSelector(selectors.selectCurrentSection);
  const currentQuestionResponse = useSelector(selectors.selectCurrentQuestionResponse);
  const [sortState, setSortState] = useState<SortState>(null);
  // const { isLoading } = useGetSessionQuery(
  //   { session_id: currentSession.id ?? '' },
  //   { skip: !currentSession.id, refetchOnMountOrArgChange: true },
  // );

  const incompleteCount = useMemo(
    () =>
      currentSection?.questions.filter((question) => question.status === 'InComplete' || question.status === 'UnSeen')
        .length,
    [currentSection],
  );

  const navigateToQuestion = (questionId: Question['id'] | null) => () => {
    onEnd?.();
    navigateTo(questionId);
  };

  const handleSort = (column: SortColumn) => () => {
    setSortState((prev) =>
      prev?.column === column
        ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: 'asc' },
    );
  };

  const sortedQuestions = useMemo(() => {
    if (!currentSection) return [];
    const indexed = currentSection.questions.map((question, originalIndex) => ({ question, originalIndex }));
    if (!sortState) return indexed;

    const { column, direction } = sortState;
    const sign = direction === 'asc' ? 1 : -1;

    return [...indexed].sort((a, b) => {
      let diff: number;
      if (column === 'question') {
        diff = a.originalIndex - b.originalIndex;
      } else if (column === 'status') {
        diff = STATUS_ORDINAL[a.question.status] - STATUS_ORDINAL[b.question.status];
      } else {
        diff = flagOrdinal(a.question.flagged) - flagOrdinal(b.question.flagged);
      }
      if (diff === 0) diff = a.originalIndex - b.originalIndex;
      return sign * diff;
    });
  }, [currentSection, sortState]);

  const renderArrow = (column: SortColumn) => {
    if (sortState?.column !== column) return null;
    return (
      <span
        className={classNames('navigator__sort-arrow', {
          'navigator__sort-arrow--asc': sortState.direction === 'asc',
          'navigator__sort-arrow--desc': sortState.direction === 'desc',
        })}
        aria-hidden="true"
      />
    );
  };

  const ariaSort = (column: SortColumn): 'ascending' | 'descending' | 'none' =>
    sortState?.column !== column ? 'none' : sortState.direction === 'asc' ? 'ascending' : 'descending';

  // if (isLoading) {
  //   return null;
  // }

  return (
    <div className="navigator__container">
      <div className="navigator__content">
        <table className="navigator__table" border={1}>
          <thead>
            <tr>
              <th
                className="navigator__sort-head"
                aria-sort={ariaSort('question')}
                onClick={handleSort('question')}
              >
                Question #{renderArrow('question')}
              </th>
              <th
                className="navigator__sort-head"
                aria-sort={ariaSort('status')}
                onClick={handleSort('status')}
              >
                Status{renderArrow('status')}
              </th>
              <th
                className="navigator__sort-head"
                aria-sort={ariaSort('flagged')}
                onClick={handleSort('flagged')}
              >
                Flagged - Review{renderArrow('flagged')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedQuestions.map(({ question, originalIndex }) => (
              <tr
                key={originalIndex}
                className={classNames({
                  'navigator__item-row': true,
                  'navigator__item-row--current': currentQuestionResponse?.question_id === question.id,
                })}
              >
                <td>
                  <a className="navigator__item-link" onClick={navigateToQuestion(question.id)}>
                    {`Question ${originalIndex + 1}`}
                  </a>
                </td>
                <td
                  className={classNames({
                    'navigator__item-status': true,
                    'navigator__item-status--incomplete': question.status === 'InComplete',
                    'navigator__item-status--unseen': question.status === 'UnSeen',
                  })}
                >
                  {(question.status === 'InComplete' || question.status === 'UnSeen') && question.status}
                </td>
                <td>{Boolean(question.flagged) && <span className="navigator__item-flagged"></span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="navigator__footer">{incompleteCount} Unseen/Incomplete</div>
    </div>
  );
};
